import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { debounce } from 'lodash';
import { useI18n } from 'vue-i18n';
import { useAppContext } from '@/composables/useAppContext';
import { useItemsGrid } from '@/composables/useItemsGrid';
import { useCategoryChart } from '@/charts/configs/useCategoryChart';
import { useDrawingCategoryChart } from '@/charts/configs/useDrawingCategoryChart';
import { useTeamsUsersChart } from '@/charts/configs/useTeamsUsersChart';

export default () => {
  const store = useStore();
  const { t } = useI18n();
  const dataLoaded = computed(() => store.getters.getDataLoaded);
  const conn = store.getters.getDBConnection;
  const filterContext = computed(() => store.state.filterItemsContext);

  const app = useAppContext();
  const grid = useItemsGrid({ ...app, filterContext: app.filterItemsContext, t });
  const chartFontColor = computed(() => store.getters.chartFontColor);
  const selectedTeam = ref(['Un-Assigned']);

  const teamsUsersChart = useTeamsUsersChart({
    chartName: 'teamUserStackedBarChart',
    filterContext,
    conn
  });

  // Page-owned level ref: the category chart's IssueTemplate short-circuit
  // writes to it to skip the subitem level
  const categoryLevel = ref(0);
  const categoryChart = useCategoryChart({
    chartName: 'teamsCategoryStackedBarChart',
    filterContext,
    currentLevel: categoryLevel,
    conn
  });

  const drawingChart = useDrawingCategoryChart({
    chartName: 'teamsDrawingStackedBarChart',
    filterContext,
    conn
  });

  // Stats refs
  const unassignedStats = ref({ count: 0, total: 0, percentage: 0 });
  const userUnassignedStats = ref({ count: 0, total: 0, percentage: 0 });
  const draftStats = ref({ count: 0, total: 0, percentage: 0 });
  const openStats = ref({ count: 0, total: 0, percentage: 0 });
  const closedStats = ref({ count: 0, total: 0, percentage: 0 });
  const teamStats = ref({ filteredCount: 0 });

  const computeStats = async () => {
    if (!dataLoaded.value) return;
  
    const isUnassigned = selectedTeam.value[0] === 'Un-Assigned';
  
    try {
      // Query stats based on whether 'Un-Assigned' is selected or a specific team
      if (isUnassigned) {
        // Stats for unassigned view (no team selected)
        const query = `
        WITH 
        total_items AS (
          SELECT COUNT(*) AS total FROM filteredItems
        ),
        unassigned_count AS (
          SELECT COUNT(*) AS count FROM filteredItems WHERE AssigneeTeamId = ''
        ),
        status_counts AS (
          SELECT 
            COUNT(CASE WHEN st.IsDraft = TRUE THEN 1 END) AS draft_count,
            COUNT(CASE WHEN st.IsTerminal = TRUE THEN 1 END) AS closed_count,
            COUNT(CASE WHEN st.IsDraft = FALSE AND st.IsTerminal = FALSE THEN 1 END) AS open_count
          FROM filteredItems fi
          JOIN status_templates st ON fi.StatusId = st.Id
        )
        SELECT 
          t.total,
          uc.count AS unassigned_count,
          sc.draft_count,
          sc.closed_count,
          sc.open_count
        FROM 
          total_items t, 
          unassigned_count uc,
          status_counts sc
      `;
      
        const result = await conn.query(query);
        const stats = result.toArray()[0].toJSON();
      
        const total = Number(stats.total) || 0;
        const unassignedCount = Number(stats.unassigned_count) || 0;
        const draftCount = Number(stats.draft_count) || 0;
        const closedCount = Number(stats.closed_count) || 0;
        const openCount = Number(stats.open_count) || 0;
      
        unassignedStats.value = {
          count: unassignedCount,
          total: total,
          percentage: total > 0 ? (unassignedCount / total) * 100 : 0
        };
      
        draftStats.value = {
          count: draftCount,
          total: total,
          percentage: total > 0 ? (draftCount / total) * 100 : 0
        };
      
        openStats.value = {
          count: openCount,
          total: total,
          percentage: total > 0 ? (openCount / total) * 100 : 0
        };
      
        closedStats.value = {
          count: closedCount,
          total: total,
          percentage: total > 0 ? (closedCount / total) * 100 : 0
        };
      
        // Clear user unassigned stats in unassigned view
        userUnassignedStats.value = { count: 0, total: 0, percentage: 0 };
      
      } else {
        // Stats for a specific team
        const teamId = selectedTeam.value[0];
        const query = `
        WITH 
        team_items AS (
          SELECT COUNT(*) AS total FROM filteredItems WHERE AssigneeTeamId = '${teamId}'
        ),
        team_user_unassigned AS (
          SELECT COUNT(*) AS count 
          FROM filteredItems 
          WHERE AssigneeTeamId = '${teamId}' AND (AssigneeUserIds IS NULL OR AssigneeUserIds NOT LIKE '%"%')
        ),
        team_status_counts AS (
          SELECT 
            COUNT(CASE WHEN st.IsDraft = TRUE THEN 1 END) AS draft_count,
            COUNT(CASE WHEN st.IsTerminal = TRUE THEN 1 END) AS closed_count,
            COUNT(CASE WHEN st.IsDraft = FALSE AND st.IsTerminal = FALSE THEN 1 END) AS open_count
          FROM filteredItems fi
          JOIN status_templates st ON fi.StatusId = st.Id
          WHERE fi.AssigneeTeamId = '${teamId}'
        )
        SELECT 
          t.total,
          tuu.count AS user_unassigned_count,
          tsc.draft_count,
          tsc.closed_count,
          tsc.open_count
        FROM 
          team_items t, 
          team_user_unassigned tuu,
          team_status_counts tsc
      `;
      
        const result = await conn.query(query);
        const stats = result.toArray()[0].toJSON();
      
        const total = Number(stats.total) || 0;
        const userUnassignedCount = Number(stats.user_unassigned_count) || 0;
        const draftCount = Number(stats.draft_count) || 0;
        const closedCount = Number(stats.closed_count) || 0;
        const openCount = Number(stats.open_count) || 0;
      
        // Update team stats
        teamStats.value = {
          filteredCount: total
        };
      
        userUnassignedStats.value = {
          count: userUnassignedCount,
          total: total,
          percentage: total > 0 ? (userUnassignedCount / total) * 100 : 0
        };
      
        draftStats.value = {
          count: draftCount,
          total: total,
          percentage: total > 0 ? (draftCount / total) * 100 : 0
        };
      
        openStats.value = {
          count: openCount,
          total: total,
          percentage: total > 0 ? (openCount / total) * 100 : 0
        };
      
        closedStats.value = {
          count: closedCount,
          total: total,
          percentage: total > 0 ? (closedCount / total) * 100 : 0
        };
      
        // Clear unassigned stats in team view
        unassignedStats.value = { count: 0, total: 0, percentage: 0 };
      }
    } catch (error) {
      console.error("Error executing stats queries:", error);
      // Reset all stats on error
      resetStats();
    }
  };

  const resetStats = () => {
    unassignedStats.value = { count: 0, total: 0, percentage: 0 };
    userUnassignedStats.value = { count: 0, total: 0, percentage: 0 };
    draftStats.value = { count: 0, total: 0, percentage: 0 };
    openStats.value = { count: 0, total: 0, percentage: 0 };
    closedStats.value = { count: 0, total: 0, percentage: 0 };
    teamStats.value = { filteredCount: 0 };
  };

  // Debounce computeStats to avoid too many queries
  const recomputeStats = debounce(() => {
    computeStats();
  }, 100);

  // Watch for changes in filter context
  watch(
    () => filterContext.value.filtersLastUpdated,
    () => {
      recomputeStats();
    },
    { deep: true }
  );

  // Watch for changes in selected team
  watch(
    () => selectedTeam.value,
    () => {
      recomputeStats();
    }
  );

  const isMounted = ref(false);
  onMounted(async () => {
    //await store.commit('resetFilters');
    isMounted.value = true;
    // Directly compute stats on mount if data is already loaded
    if (dataLoaded.value) {
      await computeStats();
    }
  });

  return {
    dataLoaded,
    filterContext,
    grid,
    chartFontColor,
    selectedTeam,
    teamsUsersChart,
    categoryLevel,
    categoryChart,
    drawingChart,
    unassignedStats,
    userUnassignedStats,
    draftStats,
    openStats,
    closedStats,
    teamStats
  };
};
